import {useCallback, useMemo, useState} from 'react'

import type {RepositoryGroup} from '../../../services/pipelinesApi.types'
import {RepositoryGroupType} from '../../../services/pipelinesApi.types'

import type {RepositoryListItem} from './RepositoryList.types'
import {getRepositoryItem} from './RepositoryList.utils'
import useOutsideRepository from './useOutsideRepository'
import {
  getHttpsRepositoryUrlFromSsh,
  getRepositoryDetailsItemFromUrl,
} from './useOutsideRepository.utils'

function useRepositoryList(
  repositoryGroups: ReadonlyArray<RepositoryGroup>,
  onSelect: (
    url: string,
    name: string,
    branch: string,
    isPrivate: boolean,
    username?: string,
  ) => void,
) {
  const [filterValue, setFilterValue] = useState<string>('')
  const username =
    repositoryGroups.find(group => group.myGroupType === RepositoryGroupType.CURRENT_USER)?.myOwner
      .login ?? ''
  const {
    getRepositoryDefaultBranch,
    isRepositoryTestConnection,
    isRepositoryTestConnectionLoading,
  } = useOutsideRepository(filterValue, username)

  const repositoryList: ReadonlyArray<RepositoryListItem> = useMemo(() => {
    const filterActive = filterValue.trim().length > 0
    const result: RepositoryListItem[] = []
    const repositoryHtmlUrls = new Set<string>()

    repositoryGroups.forEach(({myOwner, myRepositories}) => {
      myRepositories.forEach(repo => {
        const {login, avatarUrl} = myOwner
        const {name, defaultBranch, htmlUrl} = repo
        const searchValue = filterValue.toLowerCase()
        const isFoundByRepositoryName = `${login}/${name}`.toLowerCase().includes(searchValue)
        const isFoundHtmlUrl = htmlUrl.toLowerCase().includes(searchValue)

        if (!filterActive || isFoundByRepositoryName || isFoundHtmlUrl) {
          const repositoryItem = getRepositoryItem({
            login,
            avatarUrl,
            defaultBranch,
            repositoryName: name,
            repositoryUrl: htmlUrl,
            isPrivate: repo.private,
          })

          repositoryHtmlUrls.add(htmlUrl)
          result.push(repositoryItem)
        }
      })
    })

    if (isRepositoryTestConnection && !isRepositoryTestConnectionLoading) {
      const repositoryDetailsItem = getRepositoryDetailsItemFromUrl(filterValue)

      if (repositoryDetailsItem) {
        const repositoryItem = getRepositoryItem({
          repositoryUrl: filterValue,
          isPrivate: false,
          ...repositoryDetailsItem,
        })

        result.unshift(repositoryItem)
      }
    }
    return result
  }, [filterValue, repositoryGroups, isRepositoryTestConnection, isRepositoryTestConnectionLoading])
  const onSelectItem = useCallback(
    async ({repositoryUrl, repositoryName, defaultBranch, isPrivate}: RepositoryListItem) => {
      const branch = defaultBranch ? defaultBranch : await getRepositoryDefaultBranch(repositoryUrl)
      onSelect(repositoryUrl, repositoryName, branch, isPrivate, username)
    },
    [getRepositoryDefaultBranch, onSelect, username],
  )
  const changeValueInputHandler = (value: string) => {
    const repositoryUrl = getHttpsRepositoryUrlFromSsh(value)

    setFilterValue(repositoryUrl)
  }
  const isAvailableRepositories = repositoryList.length || isRepositoryTestConnectionLoading

  return {
    repositoryList,
    changeValueInputHandler,
    filterValue,
    onSelectItem,
    isAvailableRepositories,
    isRepositoryTestConnectionLoading,
  }
}

export default useRepositoryList
